import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SAVEABLE_PATHS = new Set([
  '/work-orders', '/assets', '/inspections', '/operations',
  '/vendors', '/ppm', '/budget',
]);

/**
 * On mount of a list page (when the URL has NO query string), looks up the
 * user's pinned default saved view for that path and silently navigates to
 * `path?query_string`. Idempotent per session: a one-time guard avoids
 * re-applying after the user has manually cleared filters.
 */
export const useApplyDefaultView = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const appliedRef = useRef(new Set());

  useEffect(() => {
    if (!user) return;
    if (!SAVEABLE_PATHS.has(location.pathname)) return;
    if (location.search) return; // user provided their own filters
    if (appliedRef.current.has(location.pathname)) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    let cancelled = false;
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        // Personal default first; fall back to team default
        const [personalRes, teamRes] = await Promise.all([
          axios.get(`${API_URL}/api/saved-views-defaults`, { headers }),
          axios.get(`${API_URL}/api/saved-views-team-defaults`, { headers }),
        ]);
        const personal = personalRes.data || {};
        const team = teamRes.data || {};
        const viewId = personal[location.pathname] || team[location.pathname];
        if (!viewId || cancelled) return;
        const { data: views } = await axios.get(`${API_URL}/api/saved-views`, { headers });
        const view = views.find((v) => v.id === viewId);
        if (!view || !view.query_string || cancelled) return;
        appliedRef.current.add(location.pathname);
        navigate(`${location.pathname}?${view.query_string}`, { replace: true });
      } catch (err) {
        logger.debug('apply default view failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user, location.pathname, location.search, navigate]);
};
